import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, Inject } from '@angular/core';
import { Headers, Http, RequestOptions } from '@angular/http';
import { Router } from '@angular/router';
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { ConfigService } from './config.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Rx';
import { Subject } from 'rxjs/Subject';

import { User } from 'models/user';
import { ToasterService } from 'angular2-toaster';
import { JQ_TOKEN } from './jquery.service';
import { Intercom } from 'ng-intercom';

interface UserData {
  locations: any; // actually array of locations
  customer_id: string;
  customer_name: string;
  user_id: string;
  cognito_id: string;
  data: {
    hotels: [
      {
        id: string;
      }
    ];
  };
}

@Injectable()
export class AuthService {
  isDevMode = false;

  // TODO: refactor config methods to make cleaner, possibly with config service
  UserPoolId = this.config.getConfig('region') + '_' + this.config.getConfig('userPool');
  ClientId = this.config.getConfig('clientID');
  CognitoUrl = `cognito-idp.${this.config.getConfig('region')}.amazonaws.com/${this.UserPoolId}`;
  IdentityPoolId = this.config.getConfig('region') + ':' + this.config.getConfig('identityPool');

  userPoolObject = new CognitoUserPool({
    UserPoolId: this.UserPoolId,
    ClientId: this.ClientId
  });

  stage = this.config.getConfig('stage');
  oldAuthAPIBaseUrl = this.config.getBaseUrl();
  newUsersAPIBaseUrl = this.config.getUsersUrl();

  errorMessage = new Subject<string>();
  sessionSubject = new BehaviorSubject<any>({});

  constructor(
    private router: Router,
    private config: ConfigService,
    private http: Http, // remove duplication eventually, now using http for non-protected routes
    private ngZone: NgZone,
    private httpClient: HttpClient,
    private toasterService: ToasterService,
    @Inject(JQ_TOKEN) private $: any,
    private intercom: Intercom,
  ) {}

  /**
   * Helpers
   */

  toggleDevMode() {
    if (this.isAdmin()) {
      this.isDevMode = !this.isDevMode;
    }
  }

  isNormalUser() {
    const idTokenPayload = this.sessionSubject.value;
    try {
      const userGroups = idTokenPayload.getIdToken().decodePayload()['cognito:groups'];
      return userGroups.length === 1 && userGroups.includes('Users');
    } catch (e) {
      return false;
    }
  }

  isInstallerOnly() {
    const idTokenPayload = this.sessionSubject.value;
    try {
      const userGroups = idTokenPayload.getIdToken().decodePayload()['cognito:groups'];
      return userGroups.length === 1 && userGroups.includes('Installer');
    } catch (e) {
      return false;
    }
  }

  isAdmin() {
    const idTokenPayload = this.sessionSubject.value;
    try {
      const userGroups = idTokenPayload.getIdToken().decodePayload()['cognito:groups'];
      return userGroups.includes('Admin');
    } catch (e) {
      return false;
    }
  }

  isUser() {
    const idTokenPayload = this.sessionSubject.value;
    try {
      const userGroups = idTokenPayload.getIdToken().decodePayload()['cognito:groups'];
      return userGroups.includes('Users');
    } catch (e) {
      return false;
    }
  }

  isClientAdmin() {
    const idTokenPayload = this.sessionSubject.value;
    try {
      const userGroups = idTokenPayload.getIdToken().decodePayload()['cognito:groups'];
      return userGroups.includes('Client_Admin');
    } catch (e) {
      return false;
    }
  }

  isPasswordExpired() {
    const idTokenPayload = this.sessionSubject.value;
    const passwordExpiryDaysRaw = localStorage.getItem('passwordExpiryDays');
    const passwordLastChangedRaw = idTokenPayload.getIdToken().decodePayload()[
      'custom:passwordLastChanged'
    ];

    if (passwordExpiryDaysRaw && passwordLastChangedRaw) {
      const passwordExpiryDays = parseInt(passwordExpiryDaysRaw, 10);
      const passwordLastChanged = parseInt(passwordLastChangedRaw, 10);
      // CY: careful need to cast both to number
      // custom:passwordLastChanged stored as string as Cognito seems to only accept string fields,
      let isPasswordExpired =
        Date.now() > passwordLastChanged + passwordExpiryDays * 24 * 60 * 60 * 1000;
      return isPasswordExpired;
    }
    return false;
  }

  buildUserObject(username) {
    return new CognitoUser({
      Username: username,
      Pool: this.userPoolObject
    });
  }

  getMessage(): Observable<string> {
    return this.errorMessage.asObservable();
  }

  getCognitoIdFromToken() {
    const idTokenPayload = this.sessionSubject.value;
    const sub = idTokenPayload.getIdToken().decodePayload().sub;
    return sub;
  }

  getEmailFromToken() {
    const idTokenPayload = this.sessionSubject.value;
    const email = idTokenPayload.getIdToken().decodePayload().email;
    return email;
  }

  getClientGroupFromToken() {
    const idTokenPayload = this.sessionSubject.value;
    const clientGroup = idTokenPayload.getIdToken().decodePayload()['custom:clientGroup'];
    return clientGroup;
  }

  async afterValidSession() {
    const extraUserCreds = [this.getCredentialsLambda(), this.fetchUserP()];
    return await Promise.all(extraUserCreds);
  }

  /**
   * Authentication (login session) code
   */
  broadcastSession(sessionInfo) {
    this.sessionSubject.next(sessionInfo);
  }

  /**
   * wraps .getSession() which checks if existing session && verify if valid, else uses refreshToken to refresh (async)
   * 1) broadcasts latest sessionInfo for use in calling APIs
   * 2) returns either session true or false for routing or app OnInit
   */
  hasSessionP(): Promise<boolean> {
    return new Promise(resolve => {
      const currentUser = this.userPoolObject.getCurrentUser();
      if (currentUser) {
        currentUser.getSession((err, sessionInfo) => {
          if (err) {
            console.log(`Auth service error: ${err}`);
            return resolve(false);
          }
          this.broadcastSession(sessionInfo);
          return resolve(true);
        });
      } else {
        console.log('Auth service: There is no current user ');
        resolve(false);
      }
    });
  }

  async login(email: string, password: string): Promise<any> {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    const cognitoUser = this.buildUserObject(email);
    const self = this;

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async sessionInfo => {
          console.log('----> Received login tokens ');
          self.broadcastSession(sessionInfo);
          const clientGroup = this.getClientGroupFromToken();
          try {
            await this.fetchPasswordExpiryDays(clientGroup);
            if (this.isPasswordExpired()) {
              this.$('#password-expiry-modal').modal({ backdrop: 'static', keyboard: false }); // user cannot disable or press ESC to close
              reject(`User password has expired`);
            }
            await self.afterValidSession();
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        onFailure: error => {
          reject(error);
        }
      });
    });
  }

  async newPassword(user: User, existingPassword: string, newPassword: string) {
    const email = user.email;
    await this.checkPasswordValidity(email, newPassword);

    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: existingPassword
    });
    const cognitoUser = this.buildUserObject(email);
    const self = this;

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // newPasswordRequired callback passes you two attribute objects for reference
          // user created by admin, user has to provide new password and
          // if you check required attributes wil show we need to supply given_name, family_name
          // pass back to Cognito User Pool for saving
          const userSuppliedAttributes = {
            given_name: user.firstName,
            family_name: user.lastName
          };
          cognitoUser.completeNewPasswordChallenge(newPassword, userSuppliedAttributes, {
            onSuccess: async sessionInfo => {
              try {
                /**
                 * GOTCHA, if any of these calls fail the user will NOT be able to use the LOGIN link again
                 */
                console.log('----> Received login tokens ');
                // must broadcast session first before, next steps depend on session value
                self.broadcastSession(sessionInfo);
                const clientGroup = this.getClientGroupFromToken();
                await self.updatePasswordStatus(email, newPassword);
                await self.fetchPasswordExpiryDays(clientGroup); // just fetching for checking by authGuard , no point doing immediate check since just changed!
                if (!this.isInstallerOnly()) {
                  // TODO: refactor later , this was patched during a release
                  // skip /delegation call for intallers
                  await self.afterValidSession();
                }
                resolve();
              } catch (error) {
                reject(error);
              }
            },
            onFailure: err => {
              reject(err);
            }
          });
        },
        onSuccess: sessionInfo => {
          console.log('----> Placeholder this success not called, just for type compliance');
        },
        onFailure: error => {
          console.log('---> Some error occured when user tried to login with temp credentials');
          reject(error);
        }
      });
    });
  }

  logout(): void {
    const currentUser = this.userPoolObject.getCurrentUser();
    currentUser.signOut();
    localStorage.clear(); // call this again to clear temp credentials, apart from login tokens
    this.router.navigate(['/signin']);
    this.intercom.shutdown();
  }

  /**
   * Temp will be moved elsewhere later
   */

  fetchUserP(): Promise<any> {
    const cognitoId = this.getCognitoIdFromToken();
    const url = `${this.oldAuthAPIBaseUrl}/users/${cognitoId}`;
    console.log('Fetching cust details...');

    return this.httpClient
      .get<UserData>(url)
      .toPromise()
      .then(data => {
        localStorage.setItem('customerName', data.customer_name);
        localStorage.setItem('locations', JSON.stringify(data.locations));
        return data;
      });
  }

  async getCustomerNameP() {
    const customerName = localStorage.getItem('customerName');
    if (customerName === null) {
      try {
        const data = await this.fetchUserP();
        return data.customer_name;
      } catch (error) {
        throw error;
      }
    }
    return customerName;
  }

  /**
   * Access credentials Code
   */
  isCredentialsExpired(): boolean {
    const expiry = localStorage.getItem('expireTime');
    return new Date(expiry).getTime() < Date.now();
  }

  getCredentialsLambda() {
    const cognitoUserpoolIdToken = this.sessionSubject.value.getIdToken().getJwtToken();

    const headers = new Headers({
      Authorization: cognitoUserpoolIdToken,
      'Content-Type': 'application/json'
    });
    const options = new RequestOptions({ headers });
    const delegationUrl = `${this.oldAuthAPIBaseUrl}/delegation`;

    // resp always returns 200 to pipe error back to front end
    return this.http
      .get(delegationUrl, options)
      .toPromise()
      .then(res => res.json())
      .then(credentials => {
        if (credentials.error) {
          this.toasterService.pop(
            'error',
            'Unable to get user credentials, please contact support'
          );
          console.error('Error is ' + JSON.stringify(credentials.error));
          this.logout();
          return;
        }
        console.log('-----> storing credentials now');
        // you must read the properties in this round about way, or the secretKey will not be exposed
        localStorage.setItem('accessKeyId', credentials.data.Credentials.AccessKeyId);
        localStorage.setItem('secretKey', credentials.data.Credentials.SecretKey);
        localStorage.setItem('sessionToken', credentials.data.Credentials.SessionToken);
        localStorage.setItem('expireTime', credentials.data.Credentials.Expiration);
        this.timeRefreshCredentials();
      });
  }

  timeRefreshCredentials() {
    // run setInterval outside Angular zone, so won't break testing
    // check if need refresh credentials one every X mins
    const self = this;
    this.ngZone.runOutsideAngular(() => {
      window.setInterval(() => {
        if (self.isCredentialsExpired()) {
          self.getCredentialsLambda();
        } else {
          console.log('Credentials still valid...');
        }
      }, 15 * 60 * 1000);
    });
  }

  /**
   * Forgot password code
   */

  async forgotPassword(email: string) {
    const cognitoUser = this.buildUserObject(email);

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: data => {
          console.log('The verification code has been sent to ' + JSON.stringify(data));
          resolve();
        },
        onFailure: err => {
          reject(err);
        }
      });
    });
  }

  async confirmPassword(email: string, verificationCode: string, password: string) {
    const cognitoUser = this.buildUserObject(email);

    await this.checkPasswordValidity(email, password);

    const confirmPasswordP = () => {
      return new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(verificationCode, password, {
          onSuccess: async () => {
            try {
              // we are failing the entire call if we don't register the store password
              await this.updatePasswordStatus(email, password);
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          onFailure: err => {
            reject(err);
          }
        });
      });
    };

    await confirmPasswordP();
  }

  async resetPassword(email: string, oldPassword: string, newPassword: string) {
    const cognitoUser = this.userPoolObject.getCurrentUser();

    await this.getSessionForUser(cognitoUser); // CY: need to call this first before calling .changePassword, or will get session not found error
    await this.checkPasswordValidity(email, newPassword); // validity errors will be consumed by component

    const resetPasswordP = new Promise((resolve, reject) => {
      cognitoUser.changePassword(oldPassword, newPassword, async (error, result) => {
        try {
          if (error) {
            throw error;
          }
          await this.updatePasswordStatus(email, newPassword);
          resolve(result);
        } catch (e) {
          console.error(e);
          reject(e);
          // logging all other errors here and sending generic message
        }
      });
    });

    return resetPasswordP;
  }

  private async getSessionForUser(cognitoUser: CognitoUser) {
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          console.error(err);
          reject(`user not logged in, please try forgot password instead`);
          // this error message is so specific since only consumed by the resetPassword for now
        }
        console.log(`Got sessions`);
        resolve();
      });
    });
  }

  async checkPasswordValidity(email, password): Promise<any> {
    // fully open API since called before login
    const url = this.newUsersAPIBaseUrl + `/users` + `/validate-password`;
    const body = {
      email,
      password
    };
    // cannot use the http client which adds interceptors using the idToken
    const result = await this.http
      .post(url, body)
      .toPromise()
      .then(res => res.json());

    if (result.error) {
      throw new Error(`${result.error}`);
    }
    return true;
  }

  async updatePasswordStatus(email, password): Promise<any> {
    // fully open API since called before login for forgotPassword flow
    const url = this.newUsersAPIBaseUrl + `/users` + `/update-password-status`;
    const body = {
      email,
      password
    };
    // cannot use the http client which adds interceptors using the idToken
    return this.http.post(url, body).toPromise();
  }

  async fetchPasswordExpiryDays(clientGroup): Promise<any> {
    // this has to be an open API since we perform this check before completing login
    const url = this.newUsersAPIBaseUrl + `/users/get-password-expiry-days/${clientGroup}`;

    const result = await this.http
      .get(url)
      .toPromise()
      .then(res => res.json());
    localStorage.setItem('passwordExpiryDays', result);
    return result;
  }
}



// WEBPACK FOOTER //
// /Users/sensorflow/Documents/legacy/kalinda/Dashboard/sensorflow-dashboard/src/app/core/auth.service.ts